import { Box, Button, Grid, IconButton, LinearProgress, Stack, Tooltip, Typography } from '@mui/material';
import ProvidersList from './components/ProvidersList';
import MainCard from 'ui-component/cards/MainCard';
import SnackbarAlert from 'ui-component/Alerts/SnackbarAlerts';
import LoaderSpinner from 'ui-component/LoaderSpinner';
import { useEffect, useRef, useState } from 'react';
import ProviderFirestoreService from './services/ProvidersService';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import { showConfirmAlert } from 'ui-component/Alerts/SweetAlerts';
import { ProvidersModal } from './components/ProvidersModal';
import AddIcon from '@mui/icons-material/Add';
import FirebaseStorageService from 'firebaseService/firebaseStorage';

const ProvidersManagement = () => {
  const formikRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [providers, setProviders] = useState([]);
  const [providersDisabled, setProvidersDisabled] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [loader, setLoader] = useState(false);
  const [snackbarData, setSnackbarData] = useState({
    open: false,
    severity: 'success',
    message: '',
    duration: 3000
  });

  useEffect(() => {
    setLoading(true);
    setLoader(true);
    const handleResources = async (data, setDataFunction) => {
      const icons = await Promise.all(data.map((resource) => resource.iconSrc && FirebaseStorageService.downloadFile(resource.iconSrc)));
      data.forEach((resource, index) => (resource.icon = icons[index]));

      setDataFunction(data);
      setLoading(false);
      setLoader(false);
    };

    const unsubscribeProviders = ProviderFirestoreService.subscribeToResourcesProvider((data) => handleResources(data, setProviders));
    const unsubscribeProvidersDisabled = ProviderFirestoreService.subscribeToResourcesDisabledProvider((data) =>
      handleResources(data, setProvidersDisabled)
    );

    return () => {
      unsubscribeProviders();
      unsubscribeProvidersDisabled();
    };
  }, []);

  const closeSnackbar = () => {
    setSnackbarData({
      ...snackbarData,
      open: false
    });
  };

  const handleEdit = async (proveedor) => {
    setOpen(true);
    setLoader(true);
    setTimeout(() => {
      setLoader(false);
      if (formikRef.current) {
        const file = new File([proveedor.iconSrc], proveedor.iconSrc);
        file.uploaded = true;

        formikRef.current.resetForm({
          values: {
            id: proveedor.id,
            partner: proveedor.partner,
            email: proveedor.email,
            name: proveedor.name,
            lastName: proveedor.lastName,
            website: proveedor.website,
            phone: proveedor.phone,
            tag: proveedor.tag,
            icon: file
          }
        });
      }
    }, 500);
  };

  const handleRemove = async (proveedor) => {
    const confirmChange = async () => {
      try {
        setLoader(true);
        const result = await ProviderFirestoreService.deleteProvider(proveedor.id);

        const successMessage = 'Proveedor desactivado con exito!';
        const errorMessage = 'Error al desactivar proveedor:';

        setSnackbarData({
          open: true,
          severity: result.error ? 'error' : 'success',
          message: result.error ? `${errorMessage} ${result.error.message}` : `${successMessage}`
        });
      } catch (error) {
        console.error(error);
        setSnackbarData({
          open: true,
          severity: 'error',
          message: 'Error inesperado'
        });
      } finally {
        setLoader(false);
      }
    };

    showConfirmAlert(
      'Confirmar desactivacion del proveedor',
      `¿Estás seguro de desactivar el proveedor "${proveedor.partner}"?`,
      'Confirmar',
      'info',
      confirmChange
    );
  };

  const handleReactivate = async (proveedor) => {
    const confirmChange = async () => {
      try {
        setLoader(true);
        const result = await ProviderFirestoreService.reactivateProvider(proveedor.id);

        const successMessage = 'Proveedor reactivado con exito!';
        const errorMessage = 'Error al reactivar proveedor:';

        setSnackbarData({
          open: true,
          severity: result.error ? 'error' : 'success',
          message: result.error ? `${errorMessage} ${result.error.message}` : `${successMessage}`
        });
      } catch (error) {
        console.error(error);
        setSnackbarData({
          open: true,
          severity: 'error',
          message: 'Error inesperado'
        });
      } finally {
        setLoader(false);
      }
    };

    showConfirmAlert(
      'Confirmar reactivacion del proveedro',
      `¿Estás seguro de reactivar el proveedr "${proveedor.partner}"?`,
      'Confirmar',
      'info',
      confirmChange
    );
  };

  const columnsActionsActive = [
    {
      field: 'action',
      headerName: 'Acciones',
      flex: 1.5,
      minWidth: 150,
      renderCell: (params) => (
        <Stack direction="row" justifyContent="center">
          <Tooltip title="Editar">
            <IconButton color="primary" onClick={() => handleEdit(params.row)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Desactivar">
            <IconButton color="error" onClick={() => handleRemove(params.row)}>
              <BlockIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    }
  ];

  const columnsActionsInactive = [
    {
      field: 'action',
      headerName: 'Acciones',
      flex: 1.5,
      minWidth: 150,
      renderCell: (params) => (
        <Stack direction="row" justifyContent="center">
          <Tooltip title="Reactivar">
            <IconButton color="success" onClick={() => handleReactivate(params.row)}>
              <CheckIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    }
  ];

  const doOpenModal = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  return (
    <>
      <ProvidersModal open={open} onClose={onClose} formikRef={formikRef} setLoading={setLoading} setSnackbarData={setSnackbarData} />
      <MainCard
        title="Gestión de Proveedores"
        style={{ marginTop: '20px', paddingBottom: '20px' }}
        secondary={
          <Grid container justifyContent={'center'}>
            <Box>
              <Button variant="contained" onClick={doOpenModal} startIcon={<AddIcon />}>
                Agregar
              </Button>
            </Box>
          </Grid>
        }
      >
        <Box sx={{ width: '100%' }}>{isLoading && <LinearProgress />}</Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="h5" gutterBottom>
              Proveedores Activos
            </Typography>
            <ProvidersList rows={providers} columnsExtra={columnsActionsActive} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="h5" gutterBottom>
              Proveedores Inactivos
            </Typography>
            <ProvidersList rows={providersDisabled} columnsExtra={columnsActionsInactive} />
          </Grid>
        </Grid>
        <SnackbarAlert {...snackbarData} closeSnackbar={closeSnackbar} />
        <LoaderSpinner isOpen={loader} setIsOpen={setLoader} />
      </MainCard>
    </>
  );
};

export default ProvidersManagement;
